import { useCallback, useSyncExternalStore } from "react";
import type { CkMode, CkStyle } from "@NP-Webber/crm-kit/dist/next/index.js";

/**
 * useDesign — the user's look for the (only) design: display mode + style.
 * Kept per user in localStorage; every hook instance shares one store, so the
 * shell, the personal area and the tour switch together.
 */
export type DesignChoice = { mode: CkMode; style: CkStyle };

const DEFAULT: DesignChoice = { mode: "light", style: "modern" };
const KEY = (userId: string | null) => `tb-design:${userId || "anon"}`;
const EVENT = "tb:design-change";

let currentUser: string | null = null;
let cache: DesignChoice | null = null;

function read(): DesignChoice {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(KEY(currentUser));
    const parsed = raw ? (JSON.parse(raw) as Partial<DesignChoice>) : {};
    cache = {
      mode: parsed.mode === "dark" || parsed.mode === "system" ? parsed.mode : "light",
      style: parsed.style === "classic" || parsed.style === "retro" ? parsed.style : "modern",
    };
  } catch {
    cache = { ...DEFAULT };
  }
  return cache;
}

function write(next: DesignChoice) {
  cache = next;
  try {
    localStorage.setItem(KEY(currentUser), JSON.stringify(next));
  } catch {
    /* storage blocked */
  }
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(cb: () => void) {
  window.addEventListener(EVENT, cb);
  return () => window.removeEventListener(EVENT, cb);
}

/**
 * The theme root calls this (during render) once the signed-in user is known — each user keeps their own
 * look. No event here: the root re-reads the store right after, and its subtree re-renders with it.
 */
export function setDesignUser(userId: string | null) {
  if (userId === currentUser) return;
  currentUser = userId;
  cache = null;
}

export function useDesign() {
  const choice = useSyncExternalStore(subscribe, read, read);
  const setMode = useCallback((mode: CkMode) => write({ ...read(), mode }), []);
  const setStyle = useCallback((style: CkStyle) => write({ ...read(), style }), []);
  return { ...choice, setMode, setStyle };
}

export type DesignApi = ReturnType<typeof useDesign>;
